import { useState, useEffect } from 'react';
import { api, Invite, User } from '../../api/client';
import { useToastStore } from '../../stores/toastStore';

interface InviteManagerProps {
    currentUser: User & { workspace_collaborators?: number; max_workspace_collaborators?: number };
    onClose: () => void;
}

export function InviteManager({ currentUser, onClose }: InviteManagerProps) {
    const [invites, setInvites] = useState<Invite[]>([]);
    const [collaborators, setCollaborators] = useState<User[]>([]);
    const [email, setEmail] = useState('');
    const [workspaceEmail, setWorkspaceEmail] = useState('');
    const [tier, setTier] = useState<'pro' | 'standard'>('standard');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [activeTab, setActiveTab] = useState<'workspace' | 'invite'>('workspace');

    const maxCollaborators = currentUser.max_workspace_collaborators || 2;

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [invitesData, collaboratorsData] = await Promise.all([
                currentUser.tier === 'pro' ? api.getInvites() : Promise.resolve([]),
                api.getWorkspaceCollaborators()
            ]);
            setInvites(invitesData);
            setCollaborators(collaboratorsData);
        } catch (error) {
            console.error('Failed to load data:', error);
        }
    };

    const handleInviteToSystem = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage(null);

        try {
            const result = await api.createInvite(email, tier);
            setMessage({ type: 'success', text: `Invite sent to ${email}` });
            setEmail('');
            setInvites([...invites, result.invite]);
        } catch (error) {
            setMessage({ type: 'error', text: (error as Error).message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleInviteToWorkspace = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage(null);

        try {
            const result = await api.inviteToWorkspace(workspaceEmail);
            setMessage({ type: 'success', text: result.message });
            setWorkspaceEmail('');
            loadData(); // Reload to get updated list
        } catch (error) {
            setMessage({ type: 'error', text: (error as Error).message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleRevokeInvite = async (id: string) => {
        try {
            await api.revokeInvite(id);
            setInvites(invites.filter(i => i.id !== id));
        } catch (error) {
            setMessage({ type: 'error', text: (error as Error).message });
        }
    };

    const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

    const handleRemoveCollaborator = async (userId: string) => {
        setConfirmRemoveId(null);
        try {
            await api.removeFromWorkspace(userId);
            setCollaborators(collaborators.filter(c => c.id !== userId));
            useToastStore.getState().addToast({ type: 'success', message: 'Collaborator removed' });
        } catch (error) {
            setMessage({ type: 'error', text: (error as Error).message });
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Manage Workspace</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('workspace')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'workspace'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Workspace ({collaborators.length}/{maxCollaborators})
                    </button>
                    {currentUser.tier === 'pro' && (
                        <button
                            onClick={() => setActiveTab('invite')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'invite'
                                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Invite to Platform
                        </button>
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {activeTab === 'workspace' ? (
                        <div className="space-y-6">
                            {/* Workspace description */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h3 className="font-medium text-blue-900 mb-1">Your Private Workspace</h3>
                                <p className="text-sm text-blue-700">
                                    Your documents are private. You can share access with up to {maxCollaborators} collaborators who can view and edit your documents.
                                </p>
                            </div>

                            {/* Invite to workspace */}
                            {collaborators.length < maxCollaborators ? (
                                <form onSubmit={handleInviteToWorkspace} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Add Collaborator
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="email"
                                                value={workspaceEmail}
                                                onChange={(e) => setWorkspaceEmail(e.target.value)}
                                                placeholder="Enter their email..."
                                                required
                                                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm sm:text-base"
                                            />
                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className="bg-blue-600 text-white py-2.5 px-4 sm:px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors shrink-0"
                                            >
                                                {isLoading ? '...' : 'Add'}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            They must have a Docanban account first
                                        </p>
                                    </div>
                                </form>
                            ) : (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                                    Maximum collaborators reached ({maxCollaborators})
                                </div>
                            )}

                            {message && (
                                <div className={`p-4 rounded-lg text-sm ${message.type === 'success'
                                        ? 'bg-green-50 text-green-800 border border-green-200'
                                        : 'bg-red-50 text-red-800 border border-red-200'
                                    }`}>
                                    {message.text}
                                </div>
                            )}

                            {/* Collaborators List */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-3">
                                    Workspace Collaborators
                                </h3>
                                {collaborators.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        <p>No collaborators yet</p>
                                        <p className="text-xs mt-1">Add someone to share your workspace</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {collaborators.map((user) => (
                                            <div
                                                key={user.id}
                                                className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 rounded-lg gap-2"
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div
                                                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium shrink-0"
                                                        style={{ backgroundColor: user.avatar_color }}
                                                    >
                                                        {(user.name || user.email)[0].toUpperCase()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-medium text-gray-900 truncate">
                                                            {user.name || user.email.split('@')[0]}
                                                        </div>
                                                        <div className="text-xs text-gray-500 truncate">
                                                            {user.email}
                                                        </div>
                                                    </div>
                                                </div>
                                                {confirmRemoveId === user.id ? (
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <button
                                                            onClick={() => handleRemoveCollaborator(user.id)}
                                                            className="px-2.5 py-1 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                                                        >
                                                            Confirm
                                                        </button>
                                                        <button
                                                            onClick={() => setConfirmRemoveId(null)}
                                                            className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded-lg"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setConfirmRemoveId(user.id)}
                                                        className="text-red-600 hover:text-red-700 text-sm font-medium shrink-0"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* System invite description */}
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <h3 className="font-medium text-purple-900 mb-1">Invite to Docanban</h3>
                                <p className="text-sm text-purple-700">
                                    As a Pro user, you can invite new users to join Docanban. They'll get their own private workspace.
                                </p>
                            </div>

                            {/* Invite Form */}
                            <form onSubmit={handleInviteToSystem} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="newuser@example.com"
                                        required
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm sm:text-base"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        User Tier
                                    </label>
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <label className={`flex-1 flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${tier === 'standard' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'
                                            }`}>
                                            <input
                                                type="radio"
                                                name="tier"
                                                value="standard"
                                                checked={tier === 'standard'}
                                                onChange={() => setTier('standard')}
                                                className="text-blue-600"
                                            />
                                            <div>
                                                <div className="font-medium text-gray-900">Standard</div>
                                                <div className="text-xs text-gray-500">Own workspace only</div>
                                            </div>
                                        </label>
                                        <label className={`flex-1 flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${tier === 'pro' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'
                                            }`}>
                                            <input
                                                type="radio"
                                                name="tier"
                                                value="pro"
                                                checked={tier === 'pro'}
                                                onChange={() => setTier('pro')}
                                                className="text-blue-600"
                                            />
                                            <div>
                                                <div className="font-medium text-gray-900">Pro</div>
                                                <div className="text-xs text-gray-500">Can invite others</div>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full sm:w-auto bg-purple-600 text-white py-2.5 px-6 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
                                >
                                    {isLoading ? 'Sending...' : 'Send Invite'}
                                </button>
                            </form>

                            {/* Pending Invites */}
                            {invites.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 mb-3">Pending Invites</h3>
                                    <div className="space-y-2">
                                        {invites.map((invite) => (
                                            <div
                                                key={invite.id}
                                                className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 rounded-lg gap-2"
                                            >
                                                <div className="min-w-0">
                                                    <div className="font-medium text-gray-900 truncate">{invite.email}</div>
                                                    <div className="text-xs text-gray-500">
                                                        Expires {formatDate(invite.expires_at)} · {invite.tier}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleRevokeInvite(invite.id)}
                                                    className="text-red-600 hover:text-red-700 text-sm font-medium shrink-0"
                                                >
                                                    Revoke
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
